import { SpecialDays, WeekDay } from '../dto/habit.enum';

// style
export function getThemeColor(variable: string): string {
  return getComputedStyle(document.documentElement).getPropertyValue(variable).trim();
}

// objects
export function removeNullFields<T extends { [k: string]: unknown }>(entity: T): T {
  Object.keys(entity).forEach((k) => entity[k] == null && delete entity[k]);
  return entity;
}

// math
export function round(num: number, digits: number): number {
  const factor = Math.pow(10, digits);
  return Math.round(num * factor) / factor;
}

// lists
export function sum(list: number[]): number {
  return list.reduce((prev, curr) => prev + curr, 0);
}

export function avg(list: number[]): number {
  return sum(list) / list.length;
}

// dates
export function getDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0);
}

export function getIsoDay(date: Date): string {
  return new Date(date.getTime() - date.getTimezoneOffset() * 60000).toISOString().split('T')[0];
}

export function isInCurrentWeek(date: Date) {
  const now = new Date();
  const dayOfWeek = now.getDay() || 7; // Monday

  const startOfCurrentWeek = new Date(now.setDate(now.getDate() - dayOfWeek + 1));
  startOfCurrentWeek.setHours(0, 0, 0, 0);

  const endOfCurrentWeek = new Date(startOfCurrentWeek);
  endOfCurrentWeek.setDate(startOfCurrentWeek.getDate() + 7);

  return date >= startOfCurrentWeek && date <= endOfCurrentWeek;
}

export function getWeekDay(date: Date): number {
  return (date.getDay() + 6) % 7;
}

export function getWeekDayEnum(date: Date): WeekDay {
  return Object.values(WeekDay)[getWeekDay(date)];
}

export function getWeekNumber(date: Date): number {
  const tempDate = new Date(date.getTime());
  tempDate.setHours(0, 0, 0, 0);
  tempDate.setDate(tempDate.getDate() + 4 - (tempDate.getDay() || 7)); // Thursday

  const startOfYear = new Date(tempDate.getFullYear(), 0, 1);
  return Math.ceil(((tempDate.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000) + 1) / 7);
}

export function isInCurrentMonth(date: Date) {
  const now = new Date();
  return date.getFullYear() === now.getFullYear() && date.getMonth() === now.getMonth();
}

export function getMonthDay(date: Date): number {
  return date.getDate();
}

export function getLocaleShortDate(date: Date): string {
  const userLocale = navigator.language || 'en-US';

  return new Intl.DateTimeFormat(userLocale, {
    day: 'numeric',
    month: 'numeric',
  }).format(date);
}

export function getLocaleDate(date: Date): string {
  const userLocale = navigator.language || 'en-US';

  return new Intl.DateTimeFormat(userLocale, {
    weekday: 'short',
    day: 'numeric',
    month: 'numeric',
  }).format(date);
}

export function getLocaleMonthDate(date: Date): string {
  const userLocale = navigator.language || 'en-US';

  return new Intl.DateTimeFormat(userLocale, {
    month: '2-digit',
    year: '2-digit',
  }).format(date);
}

export function isToday(date: Date): boolean {
  return isSameDay(date, new Date());
}

export function isSameDay(date1?: Date, date2?: Date): boolean {
  return (
    date1?.getFullYear() === date2?.getFullYear() &&
    date1?.getMonth() === date2?.getMonth() &&
    date1?.getDate() === date2?.getDate()
  );
}

export function daysAfter(days: number, from?: Date): Date {
  const date = from ? new Date(from) : new Date();
  date.setDate(date.getDate() + days);
  return date;
}

export function daysBefore(days: number, from?: Date): Date {
  return daysAfter(-days, from);
}

export function daysBetween(date1: Date, date2: Date) {
  const timeDiff = Math.abs(date2.getTime() - date1.getTime());
  return timeDiff / (1000 * 3600 * 24);
}

export function weeksBetween(date1: Date, date2: Date): number {
  return daysBetween(date1, date2) / 7;
}

export function weekDaysBetween(weekdays: WeekDay[], date1: Date, date2: Date): number {
  const dayCount = daysBetween(date1, date2);

  let count = 0;
  for (let i = 0; i < dayCount; i++) {
    const weekDay = getWeekDayEnum(daysAfter(i, date1));
    if (weekdays.includes(weekDay)) count++;
  }

  return count;
}

export function monthDaysBetween(monthDays: number[], date1: Date, date2: Date): number {
  const dayCount = daysBetween(date1, date2);

  let count = 0;
  for (let i = 0; i < dayCount; i++) {
    const monthDay = daysAfter(i, date1).getDate();
    if (monthDays.includes(monthDay)) count++;
  }

  return count;
}

export function specialDaysBetween(specialDays: SpecialDays, date1: Date, date2: Date): number {
  const dayCount = daysBetween(date1, date2);

  const years = yearsBetween(date1, date2);
  const emberDays = years.map((year) => getEmberDays(year)).flat();

  let count = 0;
  for (let i = 0; i < dayCount; i++) {
    const monthDay = daysAfter(i, date1);
    if (isSpecialDay(specialDays, monthDay, emberDays)) count++;
  }

  return count;
}

export function isSpecialDay(specialDays: SpecialDays, date: Date, emberDays?: Date[]): boolean {
  switch (specialDays) {
    case SpecialDays.EMBER:
      return (emberDays ?? getEmberDays(date.getFullYear())).some((emberDay) => isSameDay(emberDay, date));
    case SpecialDays.HEART_JESUS_FRIDAY:
      return date.getDay() === 5 && date.getDate() <= 7;
    case SpecialDays.HEART_MARY_SATURDAY:
      return date.getDay() === 6 && date.getDate() <= 7;
  }
}

export function monthsBetween(date1: Date, date2: Date): number {
  const [start, end] = date1 > date2 ? [date2, date1] : [date1, date2];

  const startMonth = start.getMonth();
  const startYear = start.getFullYear();

  const endMonth = end.getMonth();
  const endYear = end.getFullYear();

  const monthsDifference = (endYear - startYear) * 12 + (endMonth - startMonth);

  // start/end fraction
  const daysInStartMonth = new Date(startYear, startMonth + 1, 0).getDate();
  const daysInEndMonth = new Date(endYear, endMonth + 1, 0).getDate();

  const daysInStartDate = start.getDate();
  const startFraction = daysInStartDate / daysInStartMonth;

  const daysInEndDate = end.getDate();
  const endFraction = daysInEndDate / daysInEndMonth;

  return monthsDifference + (endFraction - startFraction);
}

export function yearsBetween(date1: Date, date2: Date): number[] {
  const years: number[] = [];
  const startYear = date1.getFullYear();
  const endYear = date2.getFullYear();

  for (let year = startYear; year <= endYear; year++) {
    years.push(year);
  }

  return years;
}

export function getEmberDays(year: number): Date[] {
  function getNextWednesdayAfter(date: Date): Date {
    const day = date.getDay();
    const diff = (3 - day + 7) % 7; // 3 represents Wednesday
    return new Date(date.getFullYear(), date.getMonth(), date.getDate() + diff);
  }

  function getEmberDays(startingFeast: Date): Date[] {
    const wednesday = getNextWednesdayAfter(startingFeast);
    return [
      wednesday,
      new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate() + 2), // Friday
      new Date(wednesday.getFullYear(), wednesday.getMonth(), wednesday.getDate() + 3), // Saturday
    ];
  }

  function getEasterDate(year: number): Date {
    const a = year % 19;
    const b = Math.floor(year / 100);
    const c = year % 100;
    const d = Math.floor(b / 4);
    const e = b % 4;
    const f = Math.floor((b + 8) / 25);
    const g = Math.floor((b - f + 1) / 3);
    const h = (19 * a + b - d - g + 15) % 30;
    const i = Math.floor(c / 4);
    const k = c % 4;
    const l = (32 + 2 * e + 2 * i - h - k) % 7;
    const m = Math.floor((a + 11 * h + 22 * l) / 451);
    const month = Math.floor((h + l - 7 * m + 114) / 31) - 1;
    const day = ((h + l - 7 * m + 114) % 31) + 1;
    return new Date(year, month, day);
  }

  const easter = getEasterDate(year);

  const lentStart = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() - 42);
  const pentecost = new Date(easter.getFullYear(), easter.getMonth(), easter.getDate() + 49);
  const winterStart = new Date(year, 11, 13);
  const autumnStart = new Date(year, 8, 14);

  return [
    getEmberDays(winterStart),
    getEmberDays(lentStart),
    getEmberDays(pentecost),
    getEmberDays(autumnStart),
  ].flat();
}

// time frames
export enum TimeFrame {
  WEEK = 'Week',
  MONTH = 'Month',
  QUARTER = 'Quarter',
  YEAR = 'Year',
  ALL = 'All',
}

export function getCutoffDate(tf: TimeFrame): Date {
  const now = new Date();

  switch (tf) {
    case TimeFrame.WEEK:
      return daysBefore(6);

    case TimeFrame.MONTH: {
      const oneMonthAgo = new Date(now);
      oneMonthAgo.setMonth(now.getMonth() - 1);
      oneMonthAgo.setDate(oneMonthAgo.getDate() + 1);
      return oneMonthAgo;
    }

    case TimeFrame.QUARTER: {
      const threeMonthsAgo = new Date(now);
      threeMonthsAgo.setMonth(now.getMonth() - 3);
      threeMonthsAgo.setDate(threeMonthsAgo.getDate() + 1);
      return threeMonthsAgo;
    }

    case TimeFrame.YEAR: {
      const oneYearAgo = new Date(now);
      oneYearAgo.setFullYear(now.getFullYear() - 1);
      oneYearAgo.setDate(oneYearAgo.getDate() + 1);
      return oneYearAgo;
    }

    case TimeFrame.ALL:
    default:
      return new Date(0);
  }
}
